import { Box, Header, Link } from '@amzn/awsui-components-core';
import { faComment, faHouse, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { generateProfilePictureUrl, PHONE_TOOL_URL } from '../../../../constants/AppConstants';
import { sanitizeHtml } from '../../../../utilities/SanitizeHtml';
import { WidgetConfig } from '../interface';
import { ContactInformation } from './ContactsData';

const DefaultContactsWidgetDefinition = { defaultRowSpan: 4, defaultColumnSpan: 1 };

interface ContactsWidgetProps {
  contactsTitle: string;
  description: string;
  contacts: ContactInformation[];
  officeHoursText?: string;
}

export const ContactsWidget = ({ contactsTitle, description, contacts, officeHoursText }: ContactsWidgetProps): WidgetConfig => {
  return {
    definition: DefaultContactsWidgetDefinition,
    data: {
      icon: 'list',
      title: contactsTitle,
      description: description,
      header: (
        <Header description={description}>
          <Box variant="h2">
            <FontAwesomeIcon icon={faPhone} />
            {' ' + contactsTitle}
          </Box>
        </Header>
      ),
      content: <ContactsWidgetContent contacts={contacts} officeHoursText={officeHoursText} />
    }
  };
};

const ContactItem: React.FC<{ item: ContactInformation }> = ({ item }) => {
  const aliases = [
    ...new Set([...(item.primaryContact ? [item.primaryContact.contactAlias] : []), ...(item.otherContacts?.map((c) => c.contactAlias) ?? [])])
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '0', fontSize: '13px', gap: '8px' }}>
      <span style={{ whiteSpace: 'nowrap' }}>{item.groupName}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
        {aliases.map((alias) => (
          <Link key={alias} variant="primary" href={`${PHONE_TOOL_URL}${alias}`} target="_blank" fontSize="body-s">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
              <img src={generateProfilePictureUrl(alias)} alt={alias} style={{ width: '22px', height: '22px', borderRadius: '50%' }} />
              {alias}
            </span>
          </Link>
        ))}
      </span>
    </div>
  );
};

export interface ContactsWidgetContentProps {
  officeHoursText?: string;
  contacts: ContactInformation[];
}

export const ContactsWidgetContent: React.FC<ContactsWidgetContentProps> = ({ officeHoursText, contacts }) => {
  return (
    <>
      <Box fontWeight="bold" variant="h5" margin={{ top: 'xs', bottom: 'xs' }}>
        <FontAwesomeIcon icon={faHouse} />
        {' Office Hours'}
      </Box>
      {officeHoursText ? (
        <Box fontWeight="normal" margin={{ top: 'xs', bottom: 'xs' }}>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(officeHoursText)
            }}
          />
        </Box>
      ) : (
        <Box fontWeight="normal" variant="p" margin={{ top: 'xs', bottom: 'xs' }}>
          No office hours information configured.
        </Box>
      )}
      <Box fontWeight="bold" variant="h5" margin={{ top: 'xs', bottom: 'xs' }}>
        <FontAwesomeIcon icon={faComment} />
        {' Key Contacts'}
      </Box>
      {contacts.length > 0 ? (
        contacts.map((contact) => <ContactItem key={contact.contactId} item={contact} />)
      ) : (
        <Box fontWeight="normal" variant="p" margin={{ top: 'xs', bottom: 'xs' }}>
          No contacts configured.
        </Box>
      )}
    </>
  );
};
